import { useState } from "react";
import { MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { btnGhost, btnPrimary, fmtDateTimeWIB, inp, todayWIB } from "../util";
import { Card, call, useLoad } from "./common";

const BLANK = { customer_id: null, customer_name: "", phone: "", channel: "KOMMO", lead_source: "ADS", stage: "NEW", result: "DIBALAS", next_date: "", notes: "" };

function Chips({ label, opts, value, onChange, tid }) {
    return (
        <div>
            <div className="text-[11px] text-neutral-500 mb-1">{label}</div>
            <div className="flex flex-wrap gap-1.5" data-testid={tid}>
                {Object.entries(opts).map(([k, l]) => (
                    <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${tid}-${k}`}
                        className={`px-2.5 py-1.5 text-xs rounded-sm border transition-colors ${value === k ? "bg-ink text-white border-ink" : "bg-white border-mist"}`}>{l}</button>
                ))}
            </div>
        </div>
    );
}

export function CustomerHistory({ c, meta, close }) {
    const [h] = useLoad(`/reporting/v2/ops/customers/${c.id}/history`);
    return (
        <div className="border border-mist rounded-sm p-3 text-xs space-y-1" data-testid="fu-history">
            <div className="flex justify-between"><b>{c.name} {c.phone && <span className="font-normal text-neutral-500">· {c.phone}</span>}</b><button type="button" className="underline" onClick={close} data-testid="fu-history-close">Tutup</button></div>
            {!h && <p className="text-neutral-400">Memuat…</p>}
            {h?.logs.length === 0 && <p className="text-neutral-400">Belum ada riwayat follow-up.</p>}
            {h?.logs.map((x) => (
                <div key={x.id} className="border-t border-mist/60 pt-1" data-testid={`fu-history-${x.id}`}>
                    {fmtDateTimeWIB(x.at)} · {x.user_name} · {meta.stages[x.stage]} · {meta.results[x.result]} · {meta.channels[x.channel]}{x.next_date && ` · lagi ${x.next_date}`}{x.notes && ` · ${x.notes}`}
                </div>
            ))}
        </div>
    );
}

export function CustomerSearch({ meta, onPick, pickLabel }) {
    const [q, setQ] = useState("");
    const [res, setRes] = useState(null);
    const [hist, setHist] = useState(null);
    const search = async () => { if (q.trim().length >= 2) { const r = await call(() => api.get("/reporting/v2/ops/customers", { params: { q: q.trim() } })); if (r) setRes(r); } };
    return (
        <div className="space-y-2" data-testid="fu-search">
            <div className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Cari nama / nomor WhatsApp" className={inp} data-testid="fu-search-input" />
                <button type="button" className={btnGhost} onClick={search} data-testid="fu-search-btn"><MagnifyingGlass size={14} /></button>
            </div>
            {res && res.length === 0 && <p className="text-xs text-neutral-400" data-testid="fu-search-empty">Customer belum ada.</p>}
            {res?.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-xs border-b border-mist/60 py-1" data-testid={`fu-search-result-${c.id}`}>
                    <span><b>{c.name}</b>{c.phone && ` · ${c.phone}`}{c.last && <span className="text-neutral-500"> · terakhir {fmtDateTimeWIB(c.last.at)} ({c.last.user_name})</span>}</span>
                    <span className="flex gap-2 shrink-0">
                        <button type="button" className="underline" onClick={() => setHist(c)} data-testid={`fu-search-history-${c.id}`}>Riwayat</button>
                        {onPick && <button type="button" className="underline font-semibold" onClick={() => { onPick(c); setRes(null); setQ(""); }} data-testid={`fu-search-pick-${c.id}`}>{pickLabel}</button>}
                    </span>
                </div>
            ))}
            {hist && <CustomerHistory key={hist.id} c={hist} meta={meta} close={() => setHist(null)} />}
        </div>
    );
}

function LogForm({ meta, init, onDone, cancel }) {
    const [f, setF] = useState(init || BLANK);
    const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
    const save = async () => {
        const body = { ...f, next_date: f.result === "FOLLOW_UP_LAGI" ? f.next_date || null : null };
        const r = await call(() => (init ? api.put(`/reporting/v2/ops/followups/${init.id}`, body) : api.post("/reporting/v2/ops/followups", body)), init ? "Follow-up diperbarui" : "Follow-up dicatat");
        if (r) { if (!init) setF(BLANK); onDone(); }
    };
    return (
        <div className="space-y-3" data-testid={init ? "fu-edit-form" : "fu-form"}>
            {init ? <p className="text-sm font-semibold">{init.customer_name}{init.phone && ` · ${init.phone}`}</p> : f.customer_id ? (
                <p className="text-sm" data-testid="fu-picked">Customer: <b>{f.customer_name}</b>{f.phone && ` · ${f.phone}`} <button type="button" className="underline text-xs" onClick={() => setF({ ...f, customer_id: null, customer_name: "", phone: "" })} data-testid="fu-unpick">ganti</button></p>
            ) : (
                <>
                    <CustomerSearch meta={meta} pickLabel="Pilih" onPick={(c) => setF({ ...f, customer_id: c.id, customer_name: c.name, phone: c.phone })} />
                    <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2">
                        <input value={f.customer_name} onChange={(e) => set("customer_name")(e.target.value)} placeholder="Nama customer baru" className={inp} data-testid="fu-name" />
                        <input value={f.phone} inputMode="tel" onChange={(e) => set("phone")(e.target.value)} placeholder="No. WhatsApp (disarankan)" className={inp} data-testid="fu-phone" />
                    </div>
                </>
            )}
            <Chips label="Channel" opts={meta.channels} value={f.channel} onChange={set("channel")} tid="fu-channel" />
            <Chips label="Sumber lead" opts={meta.sources} value={f.lead_source} onChange={set("lead_source")} tid="fu-source" />
            <Chips label="Stage" opts={meta.stages} value={f.stage} onChange={set("stage")} tid="fu-stage" />
            <Chips label="Hasil" opts={meta.results} value={f.result} onChange={set("result")} tid="fu-result" />
            {f.result === "FOLLOW_UP_LAGI" && <label className="block text-xs text-neutral-500">Follow-up lagi tanggal<input type="date" min={todayWIB()} value={f.next_date || ""} onChange={(e) => set("next_date")(e.target.value)} className={`${inp} mt-1`} data-testid="fu-next-date" /></label>}
            <input value={f.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Catatan (opsional)" className={inp} data-testid="fu-notes" />
            <div className="flex gap-2">
                <button type="button" className={btnPrimary} onClick={save} data-testid={init ? "fu-edit-save" : "fu-save"}>{init ? "Simpan perubahan" : "Catat follow-up"}</button>
                {cancel && <button type="button" className={btnGhost} onClick={cancel} data-testid="fu-edit-cancel">Batal</button>}
            </div>
        </div>
    );
}

function LogRow({ x, meta, editable, reload }) {
    const [edit, setEdit] = useState(false);
    if (edit) return <LogForm meta={meta} init={x} onDone={() => { setEdit(false); reload(); }} cancel={() => setEdit(false)} />;
    return (
        <div className="text-xs border-b border-mist/60 py-1.5 flex justify-between gap-2" data-testid={`fu-log-${x.id}`}>
            <span><b>{x.customer_name}</b> · {meta.stages[x.stage]} · {meta.results[x.result]} · {meta.channels[x.channel]} · {meta.sources[x.lead_source]}{x.next_date && ` · lagi ${x.next_date}`}{x.notes && ` · ${x.notes}`}
                <span className="text-neutral-400"> · {fmtDateTimeWIB(x.at)}{x.history?.length ? ` · diedit ${x.history.length}×` : ""}</span></span>
            {editable && <button type="button" className="shrink-0 underline" onClick={() => setEdit(true)} data-testid={`fu-log-edit-${x.id}`}><PencilSimple size={12} className="inline" /> Edit</button>}
        </div>
    );
}

export default function FollowupLog({ onChanged }) {
    const [meta] = useLoad("/reporting/v2/ops/meta");
    const [d, load] = useLoad("/reporting/v2/ops/followups", { date: todayWIB() });
    const reload = () => { load(); onChanged?.(); };
    if (!meta || !d) return null;
    const stat = [["Hari ini", d.count, "fu-count", ""], ["Selesai", d.completed, "fu-completed", "text-emerald-700"], ["Pending", d.pending, "fu-pending", "text-sky-700"], ["Terlambat", d.overdue, "fu-overdue", d.overdue ? "text-rose-700" : ""]];
    return (
        <Card title="Follow-up Log" testid="fu-log">
            <div className="grid grid-cols-4 gap-2 text-center mb-2">
                {stat.map(([l, v, id, c]) => <div key={id} className="border border-mist rounded-sm py-1.5" data-testid={id}><div className={`text-lg tabular-nums ${c}`}>{v}</div><div className="text-[10px] uppercase tracking-wider text-neutral-400">{l}</div></div>)}
            </div>
            <p className="text-[11px] text-neutral-500 mb-3">Hari ini = jumlah customer berbeda (beberapa aktivitas untuk customer yang sama dihitung sekali; edit tidak menambah). Selesai / Pending / Terlambat = pengingat “Follow-up lagi”. Tidak ada target harian.</p>
            <LogForm meta={meta} onDone={reload} />
            <div className="mt-4" data-testid="fu-today-list">
                {d.logs.map((x) => <LogRow key={x.id} x={x} meta={meta} editable={d.editable} reload={reload} />)}
                {d.logs.length === 0 && <p className="text-xs text-neutral-400">Belum ada follow-up hari ini.</p>}
            </div>
        </Card>
    );
}
